/**
 * Decision Engine - Policy Trigger Mapping Management
 *
 * Manages policy trigger mappings that link loss causes to coverage decisions.
 * v2 SafetyCulture format with insuring clauses, coverage status and pathways.
 */

import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { Badge, Drawer, Form, Input, InputNumber, Switch, Tag } from 'antd';
import { ArrowRightLeft, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';

type PolicyTriggerMappingFormInput = {
    lossCauseKey: string;
    primaryLossCause: string;
    secondaryLossCause: string;
    coverType: string;
    insuringClauseId: string;
    insuringClauseName: string;
    coverageStatus: string;
    defaultPathway: string;
    coverageRationale: string | null;
    coveredExample: string | null;
    investigateExample: string | null;
    potentialDeclineExample: string | null;
    sortOrder: number;
    isActive: boolean;
};

const FormItem = Form.Item<PolicyTriggerMappingFormInput>;

const COVERAGE_STATUS_COLOURS: Record<string, string> = {
    COVERED: 'green',
    CONDITIONAL: 'orange',
    EXCLUDED: 'red',
    INVESTIGATE: 'blue',
};

export const Route = createFileRoute(
    '/console/$orgId/configuration/claims-products/product-types/$productTypeId/decision-engine/policy-trigger-mapping',
)({
    staticData: {
        metadata: {
            title: 'Policy trigger mapping',
        },
    },
    component: PolicyTriggerMappingManagement,
});

function PolicyTriggerMappingManagement() {
    const { productTypeId } = useParams({ strict: false });
    const { trpc, trpcUtils } = useTrpc();
    const [form] = Form.useForm<PolicyTriggerMappingFormInput>();

    // Drawer state
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingId, setEditingId] = useState<bigint | null>(null);
    const formId = 'policy-trigger-mapping-form';

    // Fetch policy trigger mappings
    const { data, isLoading } = trpc.claim.decisionEngine.admin.listPolicyTriggerMappings.useQuery(
        { productTypeId: BigInt(productTypeId!) },
        { enabled: !!productTypeId },
    );
    const mappings = data?.rows ?? [];

    // Mutations
    const { mutate: createMapping, isPending: isCreating } =
        trpc.claim.decisionEngine.admin.createPolicyTriggerMapping.useMutation({
            onSuccess() {
                message.success('Policy trigger mapping created.');
                void trpcUtils.claim.decisionEngine.admin.listPolicyTriggerMappings.invalidate();
                void trpcUtils.claim.decisionEngine.getConfig.invalidate({ productTypeId: BigInt(productTypeId!) });
                closeDrawer();
            },
            onError(error) {
                errorMessage.show(error);
            },
        });

    const { mutate: updateMapping, isPending: isUpdating } =
        trpc.claim.decisionEngine.admin.updatePolicyTriggerMapping.useMutation({
            onSuccess() {
                message.success('Policy trigger mapping updated.');
                void trpcUtils.claim.decisionEngine.admin.listPolicyTriggerMappings.invalidate();
                void trpcUtils.claim.decisionEngine.getConfig.invalidate({ productTypeId: BigInt(productTypeId!) });
                closeDrawer();
            },
            onError(error) {
                errorMessage.show(error);
            },
        });

    const { mutate: deleteMapping } = trpc.claim.decisionEngine.admin.deletePolicyTriggerMapping.useMutation({
        onSuccess() {
            message.success('Policy trigger mapping deleted.');
            void trpcUtils.claim.decisionEngine.admin.listPolicyTriggerMappings.invalidate();
            void trpcUtils.claim.decisionEngine.getConfig.invalidate({ productTypeId: BigInt(productTypeId!) });
        },
        onError(error) {
            errorMessage.show(error);
        },
    });

    // Table columns
    const columnHelper = createColumnHelper<(typeof mappings)[number]>();

    const columns = useMemo(
        () => [
            columnHelper.accessor('lossCauseKey', {
                id: 'lossCauseKey',
                header: 'Loss cause key',
                width: 160,
                cell: ({ row }) => <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{row.lossCauseKey}</code>,
            }),
            columnHelper.accessor('coverType', {
                id: 'coverType',
                header: 'Cover type',
                width: 160,
            }),
            columnHelper.accessor('insuringClauseId', {
                id: 'insuringClauseId',
                header: 'Clause ID',
                width: 120,
            }),
            columnHelper.accessor('coverageStatus', {
                id: 'coverageStatus',
                header: 'Coverage',
                width: 130,
                cell: ({ row }) => (
                    <Tag color={COVERAGE_STATUS_COLOURS[row.coverageStatus] ?? 'default'}>{row.coverageStatus}</Tag>
                ),
            }),
            columnHelper.accessor('defaultPathway', {
                id: 'defaultPathway',
                header: 'Pathway',
                width: 160,
            }),
            columnHelper.accessor('isActive', {
                id: 'isActive',
                header: 'Status',
                width: 100,
                cell: ({ row }) => (
                    <Badge status={row.isActive ? 'success' : 'default'} text={row.isActive ? 'Active' : 'Inactive'} />
                ),
            }),
            columnHelper.actions({
                id: 'actions',
                cell: ({ row }) => (
                    <Space size="small">
                        <Button.Edit onClick={() => openEditDrawer(row)} />
                        <Button.Delete
                            confirmTitle="Delete policy trigger mapping"
                            confirmDescription="Are you sure you want to delete this policy trigger mapping?"
                            onConfirm={(onSuccess) =>
                                deleteMapping(
                                    { id: row.id },
                                    {
                                        onSuccess: () => {
                                            onSuccess();
                                        },
                                        onError: (error) => {
                                            errorMessage.show(error);
                                        },
                                    },
                                )
                            }
                        />
                    </Space>
                ),
                fixed: 'right',
            }),
        ],
        [columnHelper, deleteMapping],
    );

    const tableProps = usePrismaTable({
        data: mappings,
        total: data?.total ?? 0,
        loading: isLoading,
        columns,
        rowKey: 'id',
        pagination: { position: 'none' },
    });

    // Drawer handlers
    const openCreateDrawer = () => {
        setEditingId(null);
        form.resetFields();
        form.setFieldsValue({
            lossCauseKey: '',
            primaryLossCause: '',
            secondaryLossCause: '',
            coverType: '',
            insuringClauseId: '',
            insuringClauseName: '',
            coverageStatus: '',
            defaultPathway: '',
            coverageRationale: null,
            coveredExample: null,
            investigateExample: null,
            potentialDeclineExample: null,
            sortOrder: 0,
            isActive: true,
        });
        setDrawerOpen(true);
    };

    const openEditDrawer = (mapping: (typeof mappings)[number]) => {
        setEditingId(mapping.id);
        form.setFieldsValue({
            lossCauseKey: mapping.lossCauseKey,
            primaryLossCause: mapping.primaryLossCause,
            secondaryLossCause: mapping.secondaryLossCause,
            coverType: mapping.coverType,
            insuringClauseId: mapping.insuringClauseId,
            insuringClauseName: mapping.insuringClauseName,
            coverageStatus: mapping.coverageStatus,
            defaultPathway: mapping.defaultPathway,
            coverageRationale: mapping.coverageRationale,
            coveredExample: mapping.coveredExample,
            investigateExample: mapping.investigateExample,
            potentialDeclineExample: mapping.potentialDeclineExample,
            sortOrder: mapping.sortOrder,
            isActive: mapping.isActive,
        });
        setDrawerOpen(true);
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
        setEditingId(null);
        form.resetFields();
    };

    const onFinish = (values: PolicyTriggerMappingFormInput) => {
        const data = {
            lossCauseKey: values.lossCauseKey,
            primaryLossCause: values.primaryLossCause,
            secondaryLossCause: values.secondaryLossCause,
            coverType: values.coverType,
            insuringClauseId: values.insuringClauseId,
            insuringClauseName: values.insuringClauseName,
            coverageStatus: values.coverageStatus,
            defaultPathway: values.defaultPathway,
            coverageRationale: values.coverageRationale,
            coveredExample: values.coveredExample,
            investigateExample: values.investigateExample,
            potentialDeclineExample: values.potentialDeclineExample,
            sortOrder: values.sortOrder,
            isActive: values.isActive,
        };

        if (editingId) {
            updateMapping({ id: editingId, data });
        } else {
            createMapping({ productTypeId: BigInt(productTypeId!), data });
        }
    };

    return (
        <PageController route={Route}>
            <div className="mb-6 flex items-start gap-4 rounded-lg border border-violet-200 bg-violet-50 p-4 dark:border-violet-800 dark:bg-violet-950/30">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-violet-100 dark:bg-violet-900/50">
                    <ArrowRightLeft className="h-5 w-5 text-violet-600 dark:text-violet-400" />
                </div>
                <div>
                    <h3 className="mb-1 font-semibold text-sm text-violet-900 dark:text-violet-100">
                        Policy trigger mapping
                    </h3>
                    <p className="text-sm text-violet-700 dark:text-violet-300">
                        Maps loss causes to insuring clauses with coverage status and default pathways. This drives the
                        AI's coverage determination and claim routing decisions.
                    </p>
                </div>
            </div>
            <TableToolbar
                actions={
                    <Button type="primary" icon={<Plus />} onClick={openCreateDrawer}>
                        Add mapping
                    </Button>
                }
            />
            <PrismaTable {...tableProps} />

            <Drawer
                title={editingId ? 'Edit policy trigger mapping' : 'Add policy trigger mapping'}
                open={drawerOpen}
                onClose={closeDrawer}
                width={560}
                footer={
                    <div className="flex justify-end gap-2">
                        <Button onClick={closeDrawer}>Cancel</Button>
                        <Button type="primary" htmlType="submit" form={formId} loading={isCreating || isUpdating}>
                            {editingId ? 'Save changes' : 'Create'}
                        </Button>
                    </div>
                }
            >
                <Form<PolicyTriggerMappingFormInput> form={form} layout="vertical" onFinish={onFinish} id={formId}>
                    <FormItem
                        label="Loss cause key"
                        name="lossCauseKey"
                        rules={[{ required: true, message: 'Loss cause key is required' }]}
                    >
                        <Input placeholder="e.g., CLINICAL_CARE_INCIDENT" />
                    </FormItem>

                    <FormItem
                        label="Primary loss cause"
                        name="primaryLossCause"
                        rules={[{ required: true, message: 'Primary loss cause is required' }]}
                    >
                        <Input placeholder="e.g., Professional liability" />
                    </FormItem>

                    <FormItem
                        label="Secondary loss cause"
                        name="secondaryLossCause"
                        rules={[{ required: true, message: 'Secondary loss cause is required' }]}
                    >
                        <Input placeholder="e.g., Clinical care incident" />
                    </FormItem>

                    <FormItem
                        label="Cover type"
                        name="coverType"
                        rules={[{ required: true, message: 'Cover type is required' }]}
                    >
                        <Input placeholder="e.g., Professional indemnity" />
                    </FormItem>

                    <FormItem
                        label="Insuring clause ID"
                        name="insuringClauseId"
                        rules={[{ required: true, message: 'Insuring clause ID is required' }]}
                    >
                        <Input placeholder="e.g., IC001" />
                    </FormItem>

                    <FormItem
                        label="Insuring clause name"
                        name="insuringClauseName"
                        rules={[{ required: true, message: 'Insuring clause name is required' }]}
                    >
                        <Input placeholder="e.g., Professional indemnity cover" />
                    </FormItem>

                    <FormItem
                        label="Coverage status"
                        name="coverageStatus"
                        rules={[{ required: true, message: 'Coverage status is required' }]}
                    >
                        <Input placeholder="e.g., COVERED, CONDITIONAL, EXCLUDED" />
                    </FormItem>

                    <FormItem
                        label="Default pathway"
                        name="defaultPathway"
                        rules={[{ required: true, message: 'Default pathway is required' }]}
                    >
                        <Input placeholder="e.g., ACCEPT, INVESTIGATE, DECLINE" />
                    </FormItem>

                    <div className="mt-4 border-separator border-t pt-4">
                        <div className="mb-3 font-medium text-muted-foreground text-sm">Examples and rationale</div>

                        <FormItem label="Coverage rationale" name="coverageRationale">
                            <Input.TextArea rows={3} placeholder="Explanation of the coverage determination" />
                        </FormItem>

                        <FormItem label="Covered example" name="coveredExample">
                            <Input.TextArea rows={3} placeholder="Example scenario where this is covered" />
                        </FormItem>

                        <FormItem label="Investigate example" name="investigateExample">
                            <Input.TextArea rows={3} placeholder="Example scenario requiring investigation" />
                        </FormItem>

                        <FormItem label="Potential decline example" name="potentialDeclineExample">
                            <Input.TextArea rows={3} placeholder="Example scenario that may be declined" />
                        </FormItem>
                    </div>

                    <div className="mt-4 border-separator border-t pt-4">
                        <FormItem label="Sort order" name="sortOrder" tooltip="Lower numbers appear first">
                            <InputNumber min={0} className="w-full" />
                        </FormItem>

                        <FormItem label="Active" name="isActive" valuePropName="checked">
                            <Switch />
                        </FormItem>
                    </div>
                </Form>
            </Drawer>
        </PageController>
    );
}
